import React from 'react';

export const DEFAULT_RESOURCE_USES = [
  { value: 'food',           label: 'Food' },
  { value: 'hide',           label: 'Skin or hide' },
  { value: 'fur',            label: 'Fur' },
  { value: 'bones',          label: 'Bones' },
  { value: 'antlers_horns',  label: 'Antlers or horns' },
  { value: 'feathers',       label: 'Feathers' },
  { value: 'medicine',       label: 'Medicine' },
  { value: 'bait',           label: 'Bait' },
  { value: 'craft',          label: 'Craft or artwork' },
  { value: 'ceremony',       label: 'Ceremony or cultural use' },
  { value: 'shared',         label: 'Shared with family or community' },
  { value: 'sold_traded',    label: 'Sold or traded' },
  { value: 'other',          label: 'Other' },
  { value: 'prefer_not',     label: 'Prefer not to say' },
];

export const DEFAULT_OBSERVED_SIGNS = [
  { value: 'young', label: 'Young' },
  { value: 'eggs', label: 'Eggs' },
  { value: 'nests', label: 'Nests' },
  { value: 'spawning', label: 'Spawning' },
  { value: 'tracks', label: 'Tracks' },
];

const inputStyle = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', width: '100%' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: 6 };
const sectionStyle = { marginBottom: 16 };

function empty() {
  return {
    uses: [], otherUse: '', activityType: '', resourceOutcome: '',
    condition: '', sex: '', ageOrSizeClass: '',
    reproductiveSignsObserved: false, observedSigns: [], unusualConditions: '', followUpRequested: false,
  };
}

export default function MoreDetailsSection({ values = {}, category = '', onChange, harvestOnly = false, isFieldEnabled = () => true,
  resourceUseOptions = DEFAULT_RESOURCE_USES, observedSignOptions = DEFAULT_OBSERVED_SIGNS, allowUnset = false }) {
  const v = { ...empty(), ...values };
  const conditionIsPromoted = harvestOnly || category === 'fishing';
  const options = [...resourceUseOptions];
  for (const value of v.uses) {
    if (!options.some((option) => option.value === value)) {
      options.push({ value, label: DEFAULT_RESOURCE_USES.find((option) => option.value === value)?.label || value });
    }
  }
  const signs = Array.isArray(v.observedSigns) ? v.observedSigns : [];
  const signOptions = [...observedSignOptions];
  for (const value of signs) {
    if (!signOptions.some((option) => option.value === value)) {
      signOptions.push({ value, label: DEFAULT_OBSERVED_SIGNS.find((option) => option.value === value)?.label || value });
    }
  }

  const set = (field, value) => onChange({ ...v, [field]: value });

  const toggleUse = (useVal) => {
    const current = Array.isArray(v.uses) ? v.uses : [];
    const next = current.includes(useVal)
      ? current.filter((u) => u !== useVal)
      : [...current, useVal];
    set('uses', next);
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px' }}>

      {/* Resource use */}
      {isFieldEnabled('uses') && <div style={sectionStyle}>
        <label style={labelStyle}>What was this resource used for? (select all that apply)</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {options.map((u) => {
            const active = v.uses.includes(u.value);
            return (
              <button
                key={u.value}
                type="button"
                aria-pressed={active}
                onClick={() => toggleUse(u.value)}
                style={{
                  padding: '5px 12px', fontSize: 12, borderRadius: 999, cursor: 'pointer',
                  border: active ? '2px solid #16a34a' : '1px solid #cbd5e1',
                  background: active ? '#f0fdf4' : '#fff',
                  color: active ? '#166534' : '#475569',
                  fontWeight: active ? 700 : 400,
                }}
              >
                {u.label}
              </button>
            );
          })}
        </div>
        {(v.uses.includes('other') || allowUnset) && (
          <input
            style={{ ...inputStyle, marginTop: 8 }}
            placeholder="Describe other use…"
            aria-label="Other resource use"
            value={v.otherUse}
            onChange={(e) => set('otherUse', e.target.value)}
          />
        )}
      </div>}

      {/* Activity type */}
      {!harvestOnly && <div style={sectionStyle}>
        <label style={labelStyle}>Activity type</label>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {[...(allowUnset ? [['', 'Activity not specified']] : []), ['harvested','Harvested'],['observed','Observed'],['both','Both']].map(([val,lab]) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={v.activityType === val} onChange={() => set('activityType', val)} />
              {lab}
            </label>
          ))}
        </div>
      </div>}

      {/* Resource outcome */}
      {!harvestOnly && <div style={sectionStyle}>
        <label style={labelStyle}>Outcome</label>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {[...(allowUnset ? [['', 'Outcome not specified']] : []), ['kept','Kept'],['released','Released'],['left_in_place','Left in place']].map(([val,lab]) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={v.resourceOutcome === val} onChange={() => set('resourceOutcome', val)} />
              {lab}
            </label>
          ))}
        </div>
      </div>}

      {!conditionIsPromoted && isFieldEnabled('condition') && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Condition observed</label>
          <select aria-label="Condition observed" value={v.condition} onChange={(e) => set('condition', e.target.value)} style={inputStyle}>
            <option value="">Not specified</option>
            <option value="healthy">Healthy</option>
            <option value="injured">Injured</option>
            <option value="diseased_or_unusual">Diseased or unusual</option>
            <option value="dead_found">Dead when found</option>
            <option value="unknown">Not sure</option>
          </select>
        </div>
      )}

      {/* Toggles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {allowUnset && <label><input type="checkbox" checked={!!v.reproductiveSignsObserved}
          onChange={(event) => onChange({ ...v, reproductiveSignsObserved: event.target.checked,
            observedSigns: event.target.checked ? signs : [] })} /> Reproductive signs observed</label>}
        {isFieldEnabled('reproductiveSignsObserved') && <fieldset style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <legend style={labelStyle}>Signs observed (select all that apply)</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {signOptions.map((option) => <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={signs.includes(option.value)} onChange={() => {
                const next = signs.includes(option.value) ? signs.filter((value) => value !== option.value) : [...signs, option.value];
                onChange({ ...v, observedSigns: next, reproductiveSignsObserved: next.length > 0 });
              }} /> {option.label}
            </label>)}
          </div>
          {v.reproductiveSignsObserved && !signs.length && <p>Signs were previously reported without specific details.
            {' '}<button type="button" onClick={() => set('reproductiveSignsObserved', false)}>Clear previous signs report</button></p>}
        </fieldset>}
        {isFieldEnabled('followUpRequested') && <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!v.followUpRequested} onChange={(e) => set('followUpRequested', e.target.checked)} />
          I would like CIP to follow up on this record
        </label>}
      </div>

      {/* Unusual conditions */}
      {isFieldEnabled('unusualConditions') && <div style={sectionStyle}>
        <label style={labelStyle}>Anything unusual noticed in the area?</label>
        <textarea
          aria-label="Anything unusual noticed in the area?"
          value={v.unusualConditions}
          onChange={(e) => set('unusualConditions', e.target.value)}
          rows={2}
          placeholder="e.g. discolored water, dead fish, unusual smells, habitat changes…"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>}
    </div>
  );
}
